import { useState, useEffect, ChangeEvent } from "react";
import { MdViewList, MdDelete, MdEvent, MdCreate, MdEco } from "react-icons/md";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import useFilterAvancado from "../../hooks/useFilterAvancado";
import useModal from "../../hooks/useModal";
import useTalhoesStore, { TalhaoStore } from "../maps/base-map/hooks/useTalhoesStore";
import { slugify } from "../CreateEditModal/utils";
import { useDraggable } from "@dnd-kit/core";
import { SelectAntd } from "../CreateEditModal/styles";
import { useFilters } from "core/features/filters/store";

import {
  Container,
  OperationsHeader,
  OperationsIcon,
  MaterialIcons,
  OperationsTitle,
  OperationsCount,
  SelectAllContainer,
  SelectAll,
  BulkActions,
  OperationsList,
  OperationItem,
  OperationContent,
  OperationCheckbox,
  OperationDetails,
  OperationName,
  OperationMeta,
  MetaItem,
  OperationTags,
  OperationTagsOperations,
  OperationTagsTalhoes,
  OperationActions,
  ButtonIcon,
  ButtonIconDelete,
  Search
} from "./styles";
import { Icon } from "../Icon";
import { FiltrosAvancado, PlanoOperacional } from "../../interfaces/Planejamento";
import { Form, Tooltip } from "antd";
import { limitarCaracteres } from "../ModalPlanejamentoOperacoes/utils";
import { useValidatePermission } from "core/features/auth/hooks/useValidatePermission";

const { Item, useForm } = Form;

interface OperationCardProps {
  planoOperacional: any;
  selectedItems: string[];
  filters: any;
  handleSelectItem: (id: string, ocupacao: string, operacaoClasseIds: string[]) => void;
  handleEdit: (id: string, isBlockingFields: boolean) => void;
  handleDelete: (isDeleting: boolean, id: string, name: string) => void;
}

function OperationCard({
  planoOperacional,
  selectedItems,
  filters,
  handleSelectItem,
  handleEdit,
  handleDelete,
}: OperationCardProps) {

  const { planoOperacionalIds } = useTalhoesStore();
  const { hasPermission } = useValidatePermission();

  const {
    attributes,
    listeners,
    setNodeRef,
  } = useDraggable({
    disabled: !selectedItems.includes(planoOperacional.planoOperacionalId) || !hasPermission(['PlanoOperacional.VincularTalhao']),
    id: planoOperacional.planoOperacionalId,
    data: {
      selectedItems,
      planoOperacional,
    },
  });

  const safraPeriodo = planoOperacional.safraPeriodos.find((s: any) => s.safraPeriodoId === filters.safraPeriodo);
  const quantidadeTalhoes = safraPeriodo?.quantidadeTalhoes ?? 0;

  return (
    <OperationItem
      ref={setNodeRef}
      {...listeners}
      {...attributes}
    >
      <OperationContent>
        <OperationCheckbox>
          <input
            type="checkbox"
            checked={selectedItems.includes(planoOperacional.planoOperacionalId) && planoOperacionalIds.some(p => p.id === planoOperacional.planoOperacionalId)}
            onChange={() =>
              handleSelectItem(
                planoOperacional.planoOperacionalId,
                planoOperacional.ocupacao,
                planoOperacional.operacaoClasseIds
              )}
            disabled={!hasPermission(['PlanoOperacional.Alterar'])}
          />
        </OperationCheckbox>
        <Tooltip title={`${planoOperacional.codigo} - ${planoOperacional.descricao}`}>
          <OperationDetails>
            <OperationName>{planoOperacional.codigo} - {limitarCaracteres(planoOperacional.descricao, 26)}</OperationName>
            <OperationMeta>
              <MetaItem>
                <MaterialIcons color="#757575"><MdEvent /></MaterialIcons>
                <span> {format(new Date(planoOperacional.dataCriacao), "MMM yyyy", { locale: ptBR })}</span>
              </MetaItem>
              <MetaItem>
                <MaterialIcons color="#757575"><MdEco /></MaterialIcons>
                <span>{planoOperacional.ocupacao}</span>
              </MetaItem>
            </OperationMeta>
            <OperationTags>
              <OperationTagsOperations>
                {planoOperacional?.quantidadeOperacoes ?? 0} operações
              </OperationTagsOperations>
              <OperationTagsTalhoes>
                {quantidadeTalhoes} talhões
              </OperationTagsTalhoes>
            </OperationTags>
          </OperationDetails>

        </Tooltip>
      </OperationContent>
      <OperationActions>
        <Tooltip title={!hasPermission(['PlanoOperacional.Alterar']) ? `Você não tem permissão para editar planejamento` : undefined}>
          <ButtonIcon onClick={() => {
            if (!hasPermission(['PlanoOperacional.Alterar'])) return;
            handleEdit(planoOperacional?.planoOperacionalId, planoOperacional?.quantidadeTalhoes > 0 || quantidadeTalhoes > 0)
          }} title="Editar" isDeleting={!hasPermission(['PlanoOperacional.Alterar'])}>
            <MaterialIcons color="#757575"><MdCreate /></MaterialIcons>
          </ButtonIcon>
        </Tooltip>
        <Tooltip title={!hasPermission(['PlanoOperacional.Excluir']) ? `Você não tem permissão para excluir planejamento` : undefined}>
          <ButtonIconDelete isDeleting={(quantidadeTalhoes > 0) || !hasPermission(['PlanoOperacional.Excluir'])}
            onClick={() => {
              if (!hasPermission(['PlanoOperacional.Excluir'])) return;
              handleDelete(!(quantidadeTalhoes > 0), planoOperacional?.planoOperacionalId, planoOperacional?.descricao)
            }} title="Excluir">
            <MaterialIcons color="#757575"><MdDelete /></MaterialIcons>
          </ButtonIconDelete>
        </Tooltip>
      </OperationActions>
    </OperationItem>
  );
}

type ListProps = {
  planoOperacionalData: PlanoOperacional[];
  filtersData: FiltrosAvancado
};

export function List({ planoOperacionalData = [], filtersData }: ListProps) {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [hasDeleted, setHasDeleted] = useState<string[]>([]);
  const [notDeleted, setNotHasDeleted] = useState<string[]>([]);

  const { setPeriodo } = useFilterAvancado();
  const { filters, updateField } = useFilters();
  const { filtersAdvanced, filtroExecucaoOPeracional, unidade, safra } = filters;
  const { cultura } = filtersAdvanced;
  const [safraPeriodo, setSafraPeriodo] = useState(filtersAdvanced.safraPeriodo);

  const { planoOperacionalIds, setPlanoOperacionalIds } = useTalhoesStore();
  const [form] = useForm();
  const { hasPermission } = useValidatePermission();

  const [originalList, setOriginalList] = useState<any[]>();
  const [listItem, setListItem] = useState<any[]>();
  const {
    setIsEditing,
    setId,
    setIsOpen,
    setIsDeleting,
    setIsDeletingMultiple,
    setIds,
    setIdDelete,
    setNames,
    setIsCopyingObject,
    isCopyingObject,
    setIsBlockingFields
  } = useModal();

  function handleSelectAll(event: ChangeEvent<HTMLInputElement>) {
    const isChecked = event.target.checked;

    if (isChecked) {
      if (!listItem) return;

      if (listItem.length > 0) {
        const allPlanejamentos = listItem.map(i => ({ id: i.planoOperacionalId, ocupacao: i.ocupacao, operacaoClasseIds: i.operacaoClasseIds }));
        setPlanoOperacionalIds(allPlanejamentos);

        const allIds = listItem.map((item) => item.planoOperacionalId);

        const deletedIds = listItem
          .filter((item) => item.quantidadeTalhoes === 0)
          .map((item) => item.planoOperacionalId);

        const notDeletedIds = listItem
          .filter((item) => item.quantidadeTalhoes > 0)
          .map((item) => item.planoOperacionalId);

        setSelectedItems(() => {
          const newSelected = allIds;

          const selectedCopy = listItem
            .filter((item) => newSelected.includes(item.planoOperacionalId))
            .map((item) => ({
              id: item.planoOperacionalId,
              name: item.descricao,
              label: slugify(item.descricao),
            }));

          setHasDeleted(deletedIds);
          setNotHasDeleted(notDeletedIds);
          setIsCopyingObject(selectedCopy);

          return newSelected;
        });
      }
    } else {
      setPlanoOperacionalIds([]);
      setSelectedItems([]);
      setHasDeleted([]);
      setNotHasDeleted([]);
      setIsCopyingObject([]);
    }
  }

  useEffect(() => {
    if (!filtersAdvanced?.safraPeriodo) {
      setListItem([]);
      return;
    }
    if (!filtersAdvanced?.classeOperacao?.length && !filtersAdvanced?.cultura && !filtersAdvanced?.safraPeriodo) {
      setListItem(originalList);
      return;
    }

    setListItem(prev => {
      return originalList?.filter((item) => {
        const matchPeriodo = filtersAdvanced?.safraPeriodo ? item.safraPeriodos?.some((item: any) => item.safraPeriodoId === filtersAdvanced.safraPeriodo) : true;
        const matchCultura = filtersAdvanced?.cultura ? item.ocupacaoId === filtersAdvanced.cultura : true;
        const matchClasse = filtersAdvanced?.classeOperacao?.length > 0 ? filtersAdvanced.classeOperacao.some((classeId: string) => item.operacaoClasseIds?.includes(classeId)) : true;

        return matchPeriodo && matchCultura && matchClasse;
      })
    });
  }, [filtersAdvanced.classeOperacao, filtersAdvanced.cultura, filtersAdvanced.operacao, filtersAdvanced.safraPeriodo, filtersAdvanced.safraPeriodo, originalList]);

  useEffect(() => {
    if (isCopyingObject.length === 0) {
      setSelectedItems([]);
    }
  }, [isCopyingObject]);

  useEffect(() => {
    const idsOriginal = originalList?.map(i => i.planoOperacionalId).join(',');
    const idsNew = planoOperacionalData.map(i => i.planoOperacionalId).join(',');

    if (idsOriginal !== idsNew) {
      setOriginalList(planoOperacionalData);
      setListItem(planoOperacionalData);
    }
  }, [originalList]);

  useEffect(() => {
    setOriginalList(planoOperacionalData);
  }, [planoOperacionalData]);

  useEffect(() => {
    const filtro = filtersAdvanced?.safraPeriodo;
    const periodos = filtersData?.safraPeriodos;

    if (!filtro) {
      setSafraPeriodo('');
      form.resetFields();
      return;
    }

    const temOpcao = periodos?.some(f => f.safraPeriodoId === filtro);
    setSafraPeriodo(temOpcao ? filtro : '');
    form.setFieldsValue({ periodo: temOpcao ? filtro : '' });
  }, [unidade?.id, safra?.id]);

  function handleSearchList(event: ChangeEvent<HTMLInputElement>) {
    const search = event.target.value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    if (search.trim() === "") {
      setListItem(originalList);
      return;
    }

    const result = originalList?.filter((item) =>
      item.descricao
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .includes(search)
    );

    setListItem(result);
  }

  function handleSelectItem(id: string, ocupacao: string, operacaoClasseIds: string[]) {
    setPlanoOperacionalIds((prev: TalhaoStore[]) => {
      if (prev.some(t => t.id === id)) {
        return prev.filter(p => p.id !== id);
      }
      return [...prev, { id, ocupacao, operacaoClasseIds }];
    });

    setSelectedItems((prev) => {
      const isSelected = prev.includes(id);
      const newSelected = isSelected
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id];

      const item = listItem?.find((x) => x.planoOperacionalId === id);
      const hasTalhoesZero = item?.quantidadeTalhoes === 0;
      const hasTalhoes = item?.quantidadeTalhoes > 0;

      setHasDeleted((prevDeleted) => {
        if (hasTalhoesZero) {
          return newSelected.includes(id)
            ? Array.from(new Set([...prevDeleted, id]))
            : prevDeleted.filter((x) => x !== id);
        } else {
          return prevDeleted.filter((x) => x !== id);
        }
      });

      setNotHasDeleted((prevDeleted) => {
        if (hasTalhoes) {
          return newSelected.includes(id)
            ? Array.from(new Set([...prevDeleted, id]))
            : prevDeleted.filter((x) => x !== id);
        } else {
          return prevDeleted.filter((x) => x !== id);
        }
      });

      const selectedObjects = listItem?.filter((x) =>
        newSelected.includes(x.planoOperacionalId)
      ).map((x) => ({
        id: x.planoOperacionalId,
        name: x.descricao,
        label: slugify(x.descricao),
      }));

      setIsCopyingObject(selectedObjects!);

      return newSelected;
    });
  }

  function handleDelete(isDeleting: boolean, id: string, name: string) {
    if (!isDeleting) return;
    setIdDelete(id);
    setNames([name]);
    setIsDeleting(true);
  }

  function handleDeleteAll() {
    if (hasDeleted.length === 0) return;
    if (!listItem) return;
    if (hasDeleted.length > 1) {
      setIds(hasDeleted);
      setIsDeletingMultiple(true);
    } else {
      setIsDeletingMultiple(false);
    }
    setNames(listItem
      ?.filter((item) => item.quantidadeTalhoes === 0)
      ?.map((item) => item.descricao));
    setIsDeleting(true);

  }

  function handleEdit(id: string, isBlockingFields: boolean) {
    setIsEditing(true);
    setIsBlockingFields(isBlockingFields);
    setId(id);
    setIsOpen(true);
  }

  useEffect(() => {
    setSelectedItems(planoOperacionalIds.map(p => p.id));
  }, [planoOperacionalIds]);

  return (
    <Container>
      <Form form={form}>
        <Item
          label="Período de Produção"
          name="periodo"
          rules={[{ required: true, message: "Campo Período de Produção é obrigatório" }]}
        >
          <SelectAntd
            showSearch
            optionFilterProp="label"
            value={safraPeriodo || undefined}
            onChange={(value: any) => {
              setSafraPeriodo(value);
              setPeriodo(value);
              setSelectedItems([]);
              setPlanoOperacionalIds([]);

              updateField("filtersAdvanced", {
                ...filtersAdvanced,
                safraPeriodo: value,
              });

              updateField("filtroExecucaoOPeracional", {
                ...filtroExecucaoOPeracional,
                safraPeriodos: [value],
              });
            }}
            options={(filtersData?.safraPeriodos || [])
              .filter(i => cultura ? i.ocupacaoId === cultura : true)
              .map((item: any) => ({
                label: item.nome,
                value: item.safraPeriodoId,
              }))}
          />
        </Item>
      </Form>
      <OperationsHeader>
        <OperationsIcon>
          <MaterialIcons><MdViewList /></MaterialIcons>
        </OperationsIcon>
        <OperationsTitle>Planejamentos Disponíveis</OperationsTitle>
        <OperationsCount>{listItem?.length} itens</OperationsCount>
      </OperationsHeader>

      <SelectAllContainer>
        <SelectAll>
          <input
            type="checkbox"
            onChange={handleSelectAll}
            disabled={!hasPermission(['PlanoOperacional.Excluir'])}
            checked={!!listItem?.length && selectedItems.length === listItem?.length}
          />
          <span>Selecionar todos</span>
        </SelectAll>
        <Search type="search" placeholder="Pesquisar" onChange={handleSearchList} />
        <BulkActions buttonDeleted={hasDeleted.length > 0 && notDeleted.length === 0}>
          <Tooltip title={!hasPermission(['PlanoOperacional.Excluir']) ? "Você não tem permissão para excluir" : undefined}>
            <button onClick={handleDeleteAll} disabled={!hasPermission(['PlanoOperacional.Excluir'])}>
              <Icon icon={MdDelete} color={hasDeleted.length > 0 && notDeleted.length === 0 ? "#fff" : "#9e9e9e"} />
              Excluir
            </button>
          </Tooltip>
        </BulkActions>
      </SelectAllContainer>

      <OperationsList>
        {listItem?.map((planoOperacional) => (
          <OperationCard
            key={planoOperacional?.planoOperacionalId}
            planoOperacional={planoOperacional}
            selectedItems={selectedItems}
            filters={filtersAdvanced}
            handleSelectItem={handleSelectItem}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        ))}
      </OperationsList>
    </Container>
  )
}